import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  IDirectoryNode,
  useDirectoryNodeFormMutation,
} from 'app/services/documents';
import { IProject } from 'app/services/responseTypes';
import Button from 'Components/Buttons/Button';
import { SelectInputBasic } from 'Components/Form/Basic/SelectInputBasic';
import { TextAreaBasic } from 'Components/Form/Basic/TextAreaBasic';
import { TextInputBasic } from 'Components/Form/Basic/TextInputBasic';
import ErrorMessageStyle from 'Components/Form/ErrorMessageStyle';
import FileInput from 'Components/Form/Inputs/FileInput';
import Label from 'Components/Form/Label';
import { errorToast, successToast } from 'Components/Notifications';
import { useCurrentPopup } from 'Components/Popups';
import { ClientBusinessTaskTaxReturnSelect } from 'Components/Selectors/ClientBusinessTaskTaxReturnSelect';
import { AnyObject } from 'immer/dist/internal';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { MixedSchema } from 'yup/lib/mixed';
import { useDocumentsContext } from '.';

function requiredIfDocument(schema: MixedSchema<any, AnyObject, any>) {
  return Yup.mixed().when('type', {
    is: 'Document',
    then: schema.required('required'),
  });
}

const MB = 1024 * 1024;
const MAX_FILE_SIZE = 5;

const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('required'),
  doctype: requiredIfDocument(Yup.string().trim()),
  description: Yup.string().trim(),
  file: Yup.mixed().when(['id', 'type'], {
    is: (id: number, type: IDirectoryNode['object_type']) =>
      !id && type === 'document', // can not upload file in edit
    then: (schema) => Yup.mixed().required('required'),
    // .test(
    //   'fileSize',
    //   `File must be smaller than ${MAX_FILE_SIZE}mb`,
    //   (value) => value && value.size <= MAX_FILE_SIZE * MB,
    // ),
  }),
});

const doctypeOptions = [
  { label: '1099-DIV', value: '1099-DIV' },
  { label: '1099-E', value: '1099-E' },
  { label: '1099-G', value: '1099-G' },
  { label: '1099-INT', value: '1099-INT' },
  { label: '1099-K', value: '1099-K' },
  { label: '1099-MISC', value: '1099-MISC' },
  { label: '1099-R', value: '1099-R' },
  { label: '1099-T', value: '1099-T' },
  { label: 'Form 1099', value: 'Form 1099' },
  { label: 'Schedule K-1', value: 'Schedule K-1' },
  { label: 'W-2', value: 'W-2' },
  { label: 'Other', value: 'Other' },
];

export type NodeForm = {
  id?: number;
  title: string;
  description?: string;
  doctype?: string;
  client_id?: number;
  business_id?: number;
  task_id?: number;
  project_id?: number;
  project_ids?: number[];
  projects?: IProject[];
  parent_id?: number | null;
  object_type: 'folder' | 'document';
  file?: File;
};

export default function UpdateNodeForm({
  value,
}: {
  value: Partial<IDirectoryNode>;
}) {
  const {
    lockClient,
    directory: gridDirectory,
    lockProject,
    lockTask,
    refetchSamePage,
  } = useDocumentsContext();
  const { close: closeModal, contextData } = useCurrentPopup<{
    parent: IDirectoryNode;
  }>();
  const parent = contextData?.parent;
  const isUpdateForm = !!value?.id;

  let {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<NodeForm>({
    resolver: validationSchema && yupResolver(validationSchema),
    defaultValues: {
      id: value.id,
      object_type: value.object_type,
      client_id: value.clients?.length === 1 ? value.clients[0].id : undefined,
      business_id: value.business?.id,

      task_id: value.task?.id,
      description: value.description || '',
      doctype: value.doctype,
      title: value.title,
      parent_id: value.parent_id || null,
      projects: value.projects || [],
    },
  });

  const [trigger, { isLoading }] = useDirectoryNodeFormMutation();

  function submit(form: NodeForm) {
    trigger({
      ...form,
      parent_id: value.parent_id || null,
      project_ids: form.projects?.map((p) => p.id),
    })
      .unwrap()
      .then((res) => {
        refetchSamePage();
        successToast(`${form.object_type} updated`);
        closeModal();
      })
      .catch((error) => errorToast(`Couldn't update ${form.object_type}`));
  }

  const isDocument = value?.object_type === 'document';
  const clientId = watch('client_id');
  const businessId = watch('business_id');
  const taskId = watch('task_id');
  const projects = watch('projects');

  return (
    <>
      <form
        id="node_form"
        onSubmit={handleSubmit(submit)}
        className="h-full flex-1 flex flex-col overflow-y-auto"
      >
        <div className="p-2">
          <div className={`grid gap-2 ${''}`}>
            <TextInputBasic
              register={register}
              name="title"
              type="text"
              label={`${value?.object_type} Name`}
              placeholder={`${value?.object_type} Name`}
              errors={errors}
            />

            {isDocument && (
              <SelectInputBasic
                name="doctype"
                control={control}
                searchable={false}
                errors={errors}
                label="Document Type"
                options={doctypeOptions}
                placeholder="Select Document Type"
                type="select"
              />
            )}
          </div>

          {isDocument && (
            <TextAreaBasic
              name="description"
              label="Document Description"
              placeholder="Description..."
              errors={errors}
              register={register}
            />
          )}

          <div className={`grid gap-2`}>
            <ClientBusinessTaskTaxReturnSelect
              clientId={clientId}
              businessId={businessId}
              taskId={taskId}
              lockProject={lockProject}
              lockClient={lockClient}
              withMultiProjectSelector={true}
              withProjectSelector={false}
              projects={projects}
              lockTask={lockTask}
              onChange={({ clientId, businessId, taskId, projects }) => {
                setValue('client_id', clientId);
                setValue('business_id', businessId);
                setValue('task_id', taskId);
                setValue('projects', projects);
              }}
            />
          </div>
          {isDocument && !isUpdateForm && (
            <Controller
              name="file"
              control={control}
              render={({ field }) => (
                <>
                  <Label label="Document"></Label>
                  <FileInput value={field.value} onChange={field.onChange} />
                  {errors && (
                    <ErrorMessage
                      errors={errors}
                      name="file"
                      render={({ message }) => (
                        <ErrorMessageStyle message={message} />
                      )}
                    />
                  )}
                </>
              )}
            />
          )}
        </div>
      </form>

      <div className="flex shadow-top border-gray-100 p-2 gap-2">
        <Button type="button" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          isLoading={isLoading}
          form="node_form"
          type="submit"
          theme="green-fill"
        >
          {isUpdateForm ? 'Update' : 'Create'}
        </Button>
      </div>
    </>
  );
}
